/** Android 业务通知设置说明；具体菜单名称以用户设备为准。 */

export type OemGuide = {
  id: string;
  name: string;
  subtitle: string;
  accent: string;
  intro: string;
  tips: string[];
  steps: { title: string; detail: string }[];
  links?: { label: string; url: string }[];
};

export const glossary = [
  {
    term: '通知类别',
    desc: 'Android 8 起，应用可把不同业务提醒拆成独立类别。系统可分别控制横幅、锁屏、声音和振动。',
  },
  {
    term: '横幅 / 悬浮通知',
    desc: '在屏幕顶部短暂显示的业务提醒。关闭后通知仍可能进入通知栏，但不容易及时看到。',
  },
  {
    term: '通知铃声',
    desc: '由系统按通知类别播放。是否响铃、使用何种声音，以手机系统设置为准。',
  },
  {
    term: '厂商推送与第三方推送',
    desc: '不同品牌通过各自推送通道或第三方通道送达业务通知；最终是否展示仍由系统通知权限决定。',
  },
];

export const channelSupport = [
  { device: '华为', getui: '支持', vendor: '国内不支持，仅国外支持' },
  { device: '荣耀', getui: '支持', vendor: '不支持' },
  { device: '小米', getui: '支持', vendor: '支持' },
  { device: 'OPPO', getui: '支持', vendor: '不支持' },
  { device: 'vivo', getui: '支持', vendor: '不支持' },
  { device: 'iOS', getui: '支持', vendor: '支持' },
];

export const guideTopIntro = {
  lead:
    '飞舟通过系统通知提醒审批、日程、待办和会议等业务事项。不同 Android 品牌对通知类别、后台运行和横幅展示的管理方式不同。',
  standard:
    '飞舟遵循系统通知权限与推送通道的标准方式接入，是否展示横幅、播放声音或振动，由用户授权的系统设置决定。',
  action:
    '完成应用内通知授权后，如仍希望及时看到业务提醒，请继续在手机系统设置中为飞舟打开允许通知、横幅、锁屏和声音，并允许必要的后台运行。',
};

export const whyManualSettings =
  '通知能力受系统版本、品牌策略、省电模式和用户授权共同影响。完成系统设置后，可返回飞舟检查业务通知是否正常展示。';

export const oemGuides: OemGuide[] = [
  {
    id: 'xiaomi',
    name: '小米 / Redmi（MIUI / 澎湃 OS）',
    subtitle: '注意通知过滤与省电策略',
    accent: '#ff6900',
    intro: '小米系可能对通知进行重要性排序；如业务提醒进入通知栏但未显示横幅，请检查通知过滤和类别设置。',
    tips: [
      '路径参考：设置 → 通知与状态栏 → 应用通知管理 → 飞舟。',
      '如通知不稳定，可检查省电策略，允许飞舟在必要时后台运行。',
    ],
    steps: [
      { title: '打开应用通知', detail: '在应用通知管理中找到飞舟，打开允许通知。' },
      { title: '设置通知类别', detail: '进入飞舟的通知类别，为需要及时处理的事项打开横幅、锁屏和声音。' },
      { title: '检查通知过滤', detail: '确认飞舟没有被系统标记为低优先级或被过滤。' },
      { title: '必要时调整省电策略', detail: '如提醒延迟，可检查后台运行与省电设置。' },
    ],
    links: [{ label: '小米开发者：通知过滤说明', url: 'https://dev.mi.com/xiaomihyperos/documentation/detail?pId=1577' }],
  },
  {
    id: 'huawei',
    name: '华为 / HarmonyOS',
    subtitle: '路径名称可能随系统版本略有不同',
    accent: '#c41e3a',
    intro: '进入飞舟通知管理后，按业务类别开启横幅、锁屏、声音与振动。',
    tips: [
      '常见入口：设置 → 通知和状态栏 → 应用通知管理。',
      '如找不到类别，可在设置顶部搜索“飞舟”或“通知”。',
    ],
    steps: [
      { title: '打开通知总开关', detail: '进入飞舟的通知设置，允许系统展示业务通知。' },
      { title: '展开通知类别', detail: '按需要开启横幅、锁屏、声音和振动。' },
      { title: '检查后台运行', detail: '如业务提醒延迟，请检查省电与后台管理设置。' },
    ],
    links: [{ label: '华为：管理通知和状态栏', url: 'https://consumer.huawei.com/cn/support/content/zh-cn16048647/' }],
  },
  {
    id: 'honor',
    name: '荣耀',
    subtitle: '与华为系路径相近',
    accent: '#3b82f6',
    intro: '开启飞舟业务通知，并按需要调整各通知类别的横幅、声音与振动。',
    tips: ['可在系统设置顶部直接搜索“应用通知”或“飞舟”。'],
    steps: [
      { title: '进入通知设置', detail: '在应用列表中找到飞舟并开启通知。' },
      { title: '配置业务提醒', detail: '展开通知类别，开启需要及时看到的业务提醒。' },
      { title: '检查省电限制', detail: '如通知延迟，请允许必要的后台运行。' },
    ],
  },
  {
    id: 'oppo',
    name: 'OPPO / realme / OnePlus（ColorOS 等）',
    subtitle: '在应用通知下配置类别',
    accent: '#10b981',
    intro: 'ColorOS 系通常在“通知与状态栏”中集中管理应用通知。',
    tips: ['一加与 realme 用户可在设置中搜索“通知管理”。'],
    steps: [
      { title: '进入应用通知管理', detail: '打开设置 → 通知与状态栏 → 应用通知管理。' },
      { title: '选择飞舟', detail: '允许飞舟展示通知。' },
      { title: '调整类别', detail: '为业务提醒开启横幅、锁屏、声音和振动。' },
    ],
  },
  {
    id: 'vivo',
    name: 'vivo / iQOO（OriginOS / Funtouch）',
    subtitle: '注意通知类别与后台策略',
    accent: '#8b5cf6',
    intro: '在应用通知管理中为飞舟开启业务提醒，并检查横幅和后台运行。',
    tips: ['可在设置搜索“应用通知管理”直达。'],
    steps: [
      { title: '打开应用通知', detail: '进入通知与状态栏 → 应用通知管理 → 飞舟。' },
      { title: '配置类别', detail: '开启业务提醒需要的横幅、锁屏、声音和振动。' },
      { title: '检查后台策略', detail: '如提醒延迟，请检查后台与省电设置。' },
    ],
  },
  {
    id: 'generic',
    name: '其他 Android / 类原生系统',
    subtitle: '三星、摩托罗拉等',
    accent: '#64748b',
    intro: '多数 Android 系统都可在应用信息中管理飞舟的业务通知。',
    tips: ['英文系统常见名称：Notifications → Categories / Channels。'],
    steps: [
      { title: '打开应用信息', detail: '长按飞舟图标，进入应用信息或系统设置。' },
      { title: '进入通知', detail: '找到飞舟的通知管理入口。' },
      { title: '允许业务提醒', detail: '按需要开启横幅、声音、振动和锁屏通知。' },
    ],
  },
];

export const iosBrief = {
  title: 'iOS 用户说明',
  paragraphs: [
    'iOS 可在“设置 → 通知 → 飞舟”中配置锁定屏幕、通知中心、横幅、声音和标记。',
    '如业务提醒未展示，请检查专注模式、通知摘要和系统通知权限。',
  ],
};
